import { useState, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import RAGOMaticIMG from "../img/ragomatic_001.png";
import StarSlotsIMG from "../img/starslots_001.jpg";
import PolarBearFrenzyIMG from "../img/polarbearfrenzy_001.png";
import PrayerHelperIMG from "../img/prayerhelper_001.png";
import DontGetStabbedIMG from "../img/dontgetstabbed_001.jpg";
import PizzaDeliverySurvivorsIMG from "../img/pizzadeliverysurvivors_001.jpg";
import DuluthcomIMG from "../img/duluth_001.jpg";
import rotaryIMG from "../img/rotary_001.jpg";
import steppermotorIMG from "../img/steppermotor_001.jpg";
import Row from "react-bootstrap/Row";
import ProjectModal from "./ProjectModal";
import ReactGA from "react-ga";

/**
 * Project Data Structure:
 * - title: Project name
 * - desc: Short description (shown on card and in modal)
 * - img: Single image for card display (required)
 * - alt: Image alt text
 * - technologies: Array of technology strings
 * - footerText1/2: Link labels
 * - footerURL1/2: Link URLs
 * - screenshots: Optional array of images OR mixed media objects for modal gallery
 *   Can include both images (strings) and videos (objects) in any order
 *   Examples:
 *     screenshots: [img1, img2, img3]  // All images
 *     screenshots: [img1, {type: 'video', url: '...', videoType: 'youtube', title: 'Trailer'}, img2, img3]  // Mixed
 *   If provided, modal shows carousel with all media
 *   If not provided, modal shows single img
 * - videos: Optional array of video objects (DEPRECATED - use screenshots array for inline videos)
 *   Format: [{ url: "https://youtube.com/watch?v=...", type: "youtube", title: "Trailer" }]
 *   Supported types: "youtube", "vimeo"
 *   Note: Videos in this array are appended to the end. For inline positioning, use screenshots array.
 * 
 * Example with enhanced content:
 * screenshots: [image1, image2, image3]
 * expandedDescription: "Longer detailed description..."
 * features: ["Feature 1", "Feature 2", "Feature 3"]
 * challenges: "What challenges were overcome or highlights..."
 * videos: [{ url: "https://youtube.com/watch?v=...", type: "youtube", title: "Game Trailer" }]
 */

const projectData = [
  {
    title: "RAG-O-MATIC",
    desc: `An interactive educational tool that visualizes the Retrieval-Augmented Generation (RAG) pipeline step-by-step. Watch as documents are chunked, converted to embeddings, and used to answer questions with real-time cost tracking. Built with Next.js, TypeScript, and OpenAI API.`,
    footerText1: "Live Demo",
    footerURL1: "https://ragomatic.myked.com",
    footerText2: "",
    footerURL2: "",
    img: RAGOMaticIMG,
    alt: "RAG-O-MATIC - Interactive RAG Pipeline Visualization",
    technologies: ["Next.js", "TypeScript", "TailwindCSS", "Framer Motion", "OpenAI API", "RAG Systems", "Vector Embeddings", "Semantic Search"],
    // Example: Multiple screenshots to demonstrate gallery (using other project images for demo)
    screenshots: [
      RAGOMaticIMG,
      PizzaDeliverySurvivorsIMG,
      PrayerHelperIMG,
      StarSlotsIMG,
      PolarBearFrenzyIMG
    ],
    expandedDescription: "RAG-O-MATIC breaks down the complex process of Retrieval-Augmented Generation into an interactive, step-by-step experience. Users can upload documents, watch as they're chunked and converted into vector embeddings, then see how semantic search retrieves the most relevant context for answering questions. The tool provides real-time cost tracking, showing exactly how much each API call costs, making it both educational and practical for understanding RAG system economics.",
    features: [
      "Interactive step-by-step RAG pipeline visualization",
      "Real-time cost tracking for OpenAI API usage",
      "Document chunking and embedding visualization",
      "Semantic search demonstration with relevance scoring",
      "Question-answering with context highlighting",
      "Export conversation history and costs"
    ],
    challenges: "The biggest challenge was making complex AI concepts accessible without oversimplifying. I needed to show users what's happening under the hood while keeping the interface clean and intuitive. Implementing real-time cost tracking required careful API call monitoring and state management to ensure accurate calculations throughout the pipeline execution."
  },
  {
    title: "Pizza Delivery Survivors",
    desc: `Experience the thrill of pizza delivery in a zombie apocalypse! Navigate a city overrun by zombies in this unique blend of survival, strategy, and fast-paced action. Deliver pizzas, outsmart the undead, and survive in a world where your daily job becomes a fight for survival.`,
    footerText1: "Steam Store",
    footerURL1: "https://pizzadeliverysurvivors.com",
    footerText2: "",
    footerURL2: "",
    img: PizzaDeliverySurvivorsIMG,
    alt: "Pizza Delivery Survivors - Zombie apocalypse pizza delivery game screenshot",
    technologies: ["Godot 4", "Action Roguelike", "Bullet Hell", "Steam", "2D"],
    screenshots: [
      // Trailer video as first item in carousel
      { 
        type: 'video', 
        url: 'https://youtu.be/q8q1aleFixg', 
        videoType: 'youtube', 
        title: 'Game Trailer' 
      },
      // Main project image
      PizzaDeliverySurvivorsIMG
    ],
    expandedDescription: "Pizza Delivery Survivors is an action roguelike bullet hell game released to Steam Early Access on March 31, 2025, developed by Conglomerated Industries and built entirely in Godot 4. The game puts you in the role of a pizza delivery driver navigating a city overrun by zombies. This unique blend of survival, strategy, and fast-paced action transforms your daily job into a thrilling fight for survival. Every delivery is an adventure where you must dodge hordes of zombies while completing your missions. The game features dynamic gameplay with high-intensity pizza delivery missions, an expansive arsenal of creative weapons, and a progression system with unlockable upgrades.",
    features: [
      "High-intensity pizza delivery missions with zombie survival gameplay",
      "Over 12 unique weapon types including Mop, Pizza Wheel, Unlimited Breadsticks Basket, Cheese Shakers, Oven Drop, and Broken Pizza Sign",
      "Day and night shifts across multiple maps with distinct challenges",
      "Action roguelike gameplay with bullet hell mechanics",
      "Full controller support for Xbox, DualShock, and DualSense controllers",
      "Steam Achievements (16 achievements) and Steam Cloud support",
      "Progression system with unlockable upgrades and abilities",
      "Released on Steam Early Access - $9.99"
    ],
    challenges: "As a solo developer, balancing difficulty was especially challenging because my perspective was limited - what felt balanced to me might be too hard or too easy for players. Early Access provided a way to get broader audience feedback for refining the game. Building a custom mini-game engine within Godot that felt cohesive with the main game loop required careful architecture. Creating over 12 unique weapons with different mechanics and potential synergies while maintaining balance was an iterative process. The biggest challenge was ensuring the game was both fair and fun - making sure players felt challenged but not frustrated, and that the roguelike elements provided replayability without feeling repetitive. Integrating Steam features like achievements and cloud saves while maintaining game performance required careful optimization."
  },
  {
    title: "PrayerHelper.faith",
    desc: `Craft personalized prayers with Prayer Helper - a free web app built with React, Next.js, and GPT language models. No matter your faith, find your voice in prayer with our six simple questions.`,
    footerText1: "Live Site",
    footerURL1: "https://PrayerHelper.faith",
    footerText2: "",
    footerURL2: "",
    img: PrayerHelperIMG,
    alt: "PrayerHelper.faith - AI-powered prayer crafting application",
    technologies: ["React", "Next.js", "GPT", "Spiritual Technology"],
    expandedDescription: "PrayerHelper.faith is a deeply personal project born from my own struggle with prayer in my youth. I wanted to pray but didn't know what to say, and felt shame and guilt about not 'being good at it.' This tool is a labor of love for anyone facing the same problem - removing obstacles and making prayer accessible to people who want to connect but don't know how to start. The application extends beyond web access with a Twilio hotline integration, allowing users to interact with a custom AI agent via phone, making spiritual connection accessible through multiple channels.",
    features: [
      "Web and phone access via Twilio hotline",
      "Custom AI agent for personalized prayer generation",
      "Faith-inclusive approach - works for any spiritual background",
      "Six simple questions guide users to meaningful prayers",
      "OpenAI API integration for natural language generation",
      "Firebase backend for data persistence"
    ],
    challenges: "Building an AI tool for something as personal and sensitive as prayer required careful consideration of tone, inclusivity, and authenticity. I had to ensure the AI-generated prayers felt genuine and meaningful, not generic or robotic. Creating the custom AI agent with tooling capabilities was technically complex, and integrating Twilio for phone access added another layer of complexity to the architecture. The biggest challenge was balancing technical sophistication with approachability - making sure people who struggle with prayer (like I did) wouldn't be intimidated by the technology."
  },
  {
    title: "Polar Bear Frenzy",
    desc: `Polar Bear Frenzy! A Canvas game made with Phaser. Play as a polar bear trying to get fish for their family while dodging pesky penguins. Controls: left/right arrows for movement, up arrow to jump, down arrow for claw attack.`,
    footerText1: "Live Site",
    footerURL1: "http://www.myke.wtf/polarbearfrenzy/",
    footerText2: "Github Repo",
    footerURL2: "https://github.com/gr0m17/polarbearfrenzy",
    img: PolarBearFrenzyIMG,
    alt: "Polar Bear Frenzy - Phaser-based polar bear fishing game",
    technologies: ["Phaser", "JavaScript", "Canvas", "HTML5 Games", "Browser Games"],
    expandedDescription: "Polar Bear Frenzy is a family-friendly browser game built with Phaser, a powerful HTML5 game framework. Play as a determined polar bear trying to catch fish for your family while navigating through mischievous penguins that try to stop you. Phaser provided robust game development features including physics, collision detection, sprite management, and scene handling, allowing me to focus on gameplay design and polish rather than low-level engine implementation. It's a fun, accessible game that runs smoothly in any modern browser.",
    features: [
      "Built with Phaser game framework",
      "HTML5 Canvas rendering with smooth animations",
      "Keyboard controls with intuitive arrow key mapping",
      "Phaser physics system for collision detection",
      "Family-friendly gameplay suitable for all ages",
      "Completely browser-based - no downloads required"
    ],
    challenges: "Using Phaser streamlined development significantly, but learning the framework's architecture and best practices required research and experimentation. Getting the jump physics to feel responsive and natural using Phaser's physics system involved tweaking gravity, jump velocity, and collision boundaries. One challenge was ensuring the game ran smoothly across different browsers and devices - Phaser handles a lot of this, but still required optimization and testing. Balancing game difficulty to be challenging but not frustrating was an iterative process that took multiple playtests."
  },
  {
    title: "Don't Get Stabbed",
    desc: `An interactive video experience I originally created for use with YouTube annotations. I recently remastered the experience with React, creating an engaging choose-your-own-adventure style interactive story.`,
    footerText1: "Live Site",
    footerURL1: "http://www.dontgetstabbed.online",
    footerText2: "Github Repo",
    footerURL2: "https://github.com/gr0m17/dontgetstabbed",
    img: DontGetStabbedIMG,
    alt: "Don't Get Stabbed - Interactive video experience screenshot",
    technologies: ["React", "Interactive Video", "Choose Your Own Adventure"],
    expandedDescription: "Don't Get Stabbed is an interactive video experience that showcases the evolution of web technology. Originally created for YouTube annotations (a feature that no longer exists), this dark comedy choose-your-own-adventure story has been completely remastered using React. The project demonstrates how interactive narratives can be preserved and enhanced through modern web technologies, creating an engaging experience where user choices genuinely affect the story's outcome. It's a testament to both creative storytelling and technical adaptability.",
    features: [
      "Interactive choose-your-own-adventure narrative",
      "Video branching based on user choices",
      "Modern React implementation replacing deprecated YouTube annotations",
      "Dark comedy storytelling with multiple endings",
      "Seamless video transitions between story paths",
      "State management for tracking user decisions"
    ],
    challenges: "The original project relied on YouTube annotations, which were deprecated. Remastering it meant rebuilding the entire interaction system in React while maintaining the original creative vision. Syncing video playback with React state management required careful timing and event handling. Creating smooth transitions between video segments based on user choices was technically challenging - ensuring videos loaded quickly and played seamlessly without jarring cuts. The biggest challenge was preserving the original interactive experience while modernizing the technology stack."
  },
  {
    title: "Star Slots",
    desc: `A Stardew Valley inspired slot machine I built in React with an integrated global leaderboard. Features pixel art graphics and farming-themed symbols for an authentic Stardew Valley aesthetic experience.`,
    footerText1: "Live Demo",
    footerURL1: "https://starslots.surge.sh/",
    footerText2: "Github Repo",
    footerURL2: "https://github.com/gr0m17/star_slots",
    img: StarSlotsIMG,
    alt: "Star Slots - Stardew Valley themed slot machine game",
    technologies: ["React", "Slot Machine", "Stardew Valley", "Leaderboard"],
    expandedDescription: "Star Slots is a lovingly crafted homage to Stardew Valley, capturing the charming pixel art aesthetic and farming theme in an interactive slot machine. Built entirely in React, the game features authentic pixel art graphics, farming-themed symbols, and smooth animations that replicate the feel of the beloved farming simulator. The integrated global leaderboard adds competitive elements, encouraging players to return and try for high scores while enjoying the nostalgic Stardew Valley atmosphere.",
    features: [
      "Stardew Valley inspired pixel art design",
      "Global leaderboard integration for competitive play",
      "Farming-themed symbols (parsnips, chickens, tools, etc.)",
      "Smooth React animations and state management",
      "Authentic slot machine mechanics with fair random generation",
      "Responsive design for desktop and mobile play"
    ],
    challenges: "Creating pixel-perfect graphics that matched Stardew Valley's aesthetic required careful attention to detail and understanding of pixel art principles. Implementing fair random number generation for slot outcomes was crucial - users needed to trust the system while still having fun. Building a global leaderboard system required backend infrastructure to track scores across all users. The biggest challenge was balancing the slot machine mechanics to feel rewarding without being addictive, creating a fun game that respects players while paying tribute to one of my favorite games."
  },
  {
    title: "Duluth.com",
    desc: `Duluth.com was a lifestyle website I developed with WordPress. In the first 12 months grew monthly pageviews to 30,000+ through strategic content marketing and local SEO optimization.`,
    footerText1: "site is now defunct.",
    footerURL1: "https://duluth.com",
    footerText2: "",
    footerURL2: "",
    img: DuluthcomIMG,
    alt: "Duluth.com - Lifestyle website with 30k+ monthly pageviews",
    technologies: ["WordPress", "Content Marketing", "Local SEO", "Lifestyle Media"],
    expandedDescription: "Duluth.com was a passion project that combined my technical skills with content marketing expertise. As Developer & Online Editor, I built the WordPress site from scratch and grew it from zero to over 30,000 monthly pageviews in just the first 12 months. This achievement demonstrated the power of strategic content marketing, local SEO optimization, and understanding your audience. It was an amazing experience being back in my hometown and contributing to the local community through engaging content and a well-designed platform.",
    features: [
      "WordPress development and customization",
      "Strategic content marketing campaigns",
      "Local SEO optimization for Minnesota region",
      "Multimedia content production and integration",
      "Rapid traffic growth from 0 to 30k monthly pageviews",
      "Community-focused lifestyle content creation"
    ],
    challenges: "Starting from zero pageviews meant building both the technical platform and audience simultaneously. Creating content that resonated with the local Duluth community required deep understanding of the area's culture, interests, and needs. Optimizing for local SEO in a competitive space required careful keyword research, content strategy, and technical SEO implementation. Balancing technical development with content creation was a constant juggle - I was both building the site and producing the content that would drive traffic. The biggest challenge was maintaining quality while scaling quickly to reach the 30k milestone."
  },
  {
    title: "Rotary Cellphone",
    desc: `I turned a Rotary telephone into a cellphone using a 3g chipset and an arduino. This innovative hardware hack combines vintage aesthetics with modern mobile technology, creating a unique communication device.`,
    footerText1: "Video Demo",
    footerURL1: "https://myke.wtf/adafruit_interview/",
    footerText2: "Github Repo",
    footerURL2: "https://github.com/gr0m17/rotary_cellphone_dialer",
    img: rotaryIMG,
    alt: "Rotary Cellphone - Vintage rotary phone converted to mobile phone",
    technologies: ["Arduino", "3G Chipset", "Hardware Hacking", "Vintage Tech"],
    expandedDescription: "The Rotary Cellphone is a perfect example of blending vintage aesthetics with cutting-edge technology. By integrating a 3G chipset with an Arduino microcontroller, I transformed a classic rotary telephone into a fully functional mobile phone. This hardware hack required reverse engineering the rotary dial mechanism, mapping it to modern mobile protocols, and creating custom firmware to bridge the gap between analog and digital communication. The project was featured in an Adafruit interview, showcasing the intersection of hardware hacking, creative problem-solving, and nostalgic design.",
    features: [
      "Vintage rotary phone housing with modern mobile functionality",
      "Arduino-based control system",
      "3G chipset integration for cellular connectivity",
      "Custom firmware development for dial mapping",
      "Featured in Adafruit interview",
      "Open source hardware and software documentation"
    ],
    challenges: "The biggest challenge was bridging the gap between analog rotary dial mechanics and digital mobile phone protocols. The rotary dial produces pulses that needed to be converted to DTMF tones and transmitted via the 3G chipset. Reverse engineering the rotary mechanism without damaging the vintage phone required careful disassembly and understanding of mechanical systems. Power management was tricky - fitting modern battery technology into a vintage phone case while maintaining the authentic look. Ensuring the dialing mechanism worked reliably for modern phone numbers (which are longer and include area codes) required custom logic in the Arduino firmware."
  },
  {
    title: "The Stepper Motor Music Machine",
    desc: `I turned 8 Stepper motors, a custom designed and 3d printed mount, and 2 stepper controllers into an 8 voice music machine. 75k followers and growing on TikTok, showcasing the intersection of music and robotics.`,
    footerText1: "Asgore Theme from Undertale",
    footerURL1:
      "https://www.tiktok.com/@steppermotor/video/7515919693371231519",
    footerText2: "TikTok profile",
    footerURL2: "https://www.tiktok.com/@steppermotor",
    img: steppermotorIMG,
    alt: "Stepper Motor Music Machine - 8-voice robotic music instrument",
    technologies: ["Stepper Motors", "3D Printing", "Robotics", "Music Technology", "TikTok"],
    expandedDescription: "The Stepper Motor Music Machine is a custom-built hardware instrument that combines engineering precision with musical creativity. Eight stepper motors, each serving as a voice, are controlled via MIDI and programmed using Fruity Loops, creating a unique robotic music machine. The custom-designed 3D printed mount holds everything together, while two stepper controllers manage the complex orchestration. What started as a hardware project evolved into viral content creation - filmed performances grew my TikTok channel to over 80,000 followers and generated 1M+ likes, demonstrating how creative engineering can resonate with a massive audience. The project perfectly blends my love of technology, music, and creative content.",
    features: [
      "8-voice stepper motor instrument",
      "MIDI control integration for precise music programming",
      "Custom 3D printed mount and hardware design",
      "Fruity Loops sequencing and composition",
      "TikTok viral content - 80k+ followers and 1M+ likes",
      "Featured in Adafruit interview",
      "Open source documentation for hardware and software"
    ],
    challenges: "Synchronizing eight stepper motors to create harmonious music required precise timing and careful calibration. Each motor had to hit its note at exactly the right moment, and the physical constraints of stepper motors (they can't instantly change direction or speed) meant I had to account for mechanical limitations in the music composition. The 3D printing design needed to be both functional (holding motors securely) and visually appealing (for the TikTok videos). The biggest challenge was bridging the gap between technical engineering and viral content creation - making sure the videos were both technically impressive and entertaining for a general audience. Balancing the technical complexity with accessibility in the content was key to the project's success."
  },
];

// Helper function to convert project title to anchor ID
// Matches the format used in chatbot integration docs
const getProjectAnchorId = (title) => {
  // Special mappings for projects with specific ID requirements
  const specialMappings = {
    "RAG-O-MATIC": "rag-o-matic",
    "PrayerHelper.faith": "prayerhelper",
    "Duluth.com": "duluth",
    "The Stepper Motor Music Machine": "steppermotormusic"
  };
  
  // Check if there's a special mapping first
  if (specialMappings[title]) {
    return specialMappings[title];
  }
  
  // Default conversion: lowercase, remove special chars, remove spaces, remove "the" prefix
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '') // Remove spaces
    .replace(/^the\s*/g, '') // Remove "the" prefix if at start
    .trim();
};

export default function Projects() {
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [initialImageIndex, setInitialImageIndex] = useState(0);
  const modalOpenTimeRef = useRef(null);

  // Helper function to find project by anchor ID
  const findProjectByAnchorId = (anchorId) => {
    return projectData.find(project => getProjectAnchorId(project.title) === anchorId);
  };

  // Parse hash to extract project ID and optional image index
  // Format: #rag-o-matic or #rag-o-matic-4
  const parseHash = (hash) => {
    if (!hash) return { projectId: null, imageIndex: 0 };
    
    // Try to match pattern: projectname-number
    const match = hash.match(/^(.+)-(\d+)$/);
    if (match) {
      const projectId = match[1];
      const imageNum = parseInt(match[2], 10);
      // Convert 1-based (from URL) to 0-based (for array)
      const imageIndex = Math.max(0, imageNum - 1);
      return { projectId, imageIndex };
    }
    
    // No number, just project ID
    return { projectId: hash, imageIndex: 0 };
  };

  // Helper function to scroll to card and center it (for hash links)
  const scrollToCard = (anchorId) => {
    const cardElement = document.getElementById(anchorId);
    if (cardElement) {
      const headerHeight = document.getElementById('navbar')?.offsetHeight || 80;
      const cardRect = cardElement.getBoundingClientRect();
      const cardTop = cardRect.top + window.scrollY;
      const cardCenter = cardTop - headerHeight - (window.innerHeight / 2) + (cardRect.height / 2);
      
      window.scrollTo({
        top: Math.max(0, cardCenter),
        behavior: 'smooth'
      });
    }
  };

  // Handle hash changes - detect project anchors and open modal
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      
      // Skip if hash is empty or matches main sections (handled by App.js)
      if (!hash || ['home', 'about', 'projects', 'contact'].includes(hash)) {
        return;
      }
      
      // Parse hash to get project ID and image index
      const { projectId, imageIndex } = parseHash(hash);
      
      // Check if hash matches a project anchor
      const project = findProjectByAnchorId(projectId);
      
      if (project) {
        const anchorId = getProjectAnchorId(project.title);
        const projectsSection = document.getElementById('projects');
        
        // Validate image index
        const screenshots = project.screenshots || (project.img ? [project.img] : []);
        const validImageIndex = Math.min(imageIndex, screenshots.length - 1);
        
        // First, scroll to the projects section
        if (projectsSection) {
          projectsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Then scroll to and center the specific project card
        // Use a delay to let the section scroll start
        setTimeout(() => {
          scrollToCard(anchorId);
        }, 200);
        
        // Open modal after scrolling completes
        setTimeout(() => {
          setSelectedProject(project);
          setInitialImageIndex(validImageIndex);
          modalOpenTimeRef.current = Date.now();
          setShowModal(true);
          // Track modal open via hash link with detailed context
          const screenshots = project.screenshots || (project.img ? [project.img] : []);
          ReactGA.event({
            category: 'project_modal',
            action: 'modal_opened',
            label: `${project.title}|opened_via_hash_link|image_index_${validImageIndex + 1}_of_${screenshots.length}|project_anchor_${anchorId}`,
            value: validImageIndex + 1
          });
        }, 500);
      } else {
        // Hash doesn't match a project, close modal if open
        if (showModal) {
          setShowModal(false);
          setSelectedProject(null);
        }
      }
    };

    // Check hash on mount (with delay to ensure DOM is ready)
    setTimeout(handleHashChange, 100);

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    // Also listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - we only want this to run on mount/hash changes

  // Handle opening modal from card click
  const handleCardClick = (e, project) => {
    // Check if click is on a link - if so, let it navigate normally
    if (e.target.closest('a')) {
      return;
    }

    e.preventDefault();
    const anchorId = getProjectAnchorId(project.title);
    
    // Update URL hash (always start at first image when clicking card)
    window.history.pushState(null, '', `#${anchorId}`);
    
    // Open modal at first image
    setSelectedProject(project);
    setInitialImageIndex(0);
    modalOpenTimeRef.current = Date.now();
    setShowModal(true);
    // Track modal open via card click with detailed context
    const screenshots = project.screenshots || (project.img ? [project.img] : []);
    const techCount = project.technologies ? project.technologies.length : 0;
    ReactGA.event({
      category: 'project_modal',
      action: 'modal_opened',
      label: `${project.title}|opened_via_card_click|image_index_1_of_${screenshots.length}|tech_count_${techCount}|project_anchor_${anchorId}`,
      value: 1
    });
  };

  // Handle closing modal
  const handleCloseModal = (closeMethod = 'close_button') => {
    // Track modal close with detailed context
    if (selectedProject) {
      const screenshots = selectedProject.screenshots || (selectedProject.img ? [selectedProject.img] : []);
      const timeOpen = Date.now() - (modalOpenTimeRef.current || Date.now());
      ReactGA.event({
        category: 'project_modal',
        action: 'modal_closed',
        label: `${selectedProject.title}|closed_via_${closeMethod}|final_image_index_${initialImageIndex + 1}_of_${screenshots.length}|time_open_${Math.round(timeOpen / 1000)}s|project_anchor_${getProjectAnchorId(selectedProject.title)}`,
        value: Math.round(timeOpen / 1000)
      });
    }
    setShowModal(false);
    // Leave hash as-is - don't change URL when closing modal
  };

  return (
    <div style={{ paddingTop: "10vh", paddingBottom: "5vh" }}>
      <Container>
        <p className="text-center align-middle display-1 p-3">
          Recent Projects
        </p>
        <Row className="g-3" xs={1} sm={1} md={2} lg={2} xl={3}>
          {projectData.map((project, index) => {
            const anchorId = getProjectAnchorId(project.title);
            return (
              <Col key={project.title || index}>
                <Card 
                  id={anchorId} 
                  className="h-100" 
                  style={{ scrollMarginTop: '100px', cursor: 'pointer' }}
                  onClick={(e) => handleCardClick(e, project)}
                >
                  <div style={{ position: 'relative' }}>
                    <Card.Img variant="top" src={project.img} alt={project.alt} />
                    <div 
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0)',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0)'}
                    />
                  </div>
                  <Card.Body>
                    <Card.Title>{project.title}</Card.Title>
                    <Card.Text>{project.desc}</Card.Text>
                    <div className="mt-2">
                      {project.technologies && project.technologies.map((tech, techIndex) => (
                        <span key={techIndex} className="badge bg-secondary me-1 mb-1">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </Card.Body>
                  <Card.Footer>
                    <Card.Link 
                      href={project.footerURL1}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Track footer link click with detailed context
                        const linkUrl = project.footerURL1 || 'unknown';
                        const linkType = linkUrl.includes('http') ? 'external' : 'internal';
                        ReactGA.event({
                          category: 'project_card',
                          action: 'footer_link_clicked',
                          label: `${project.title}|link_text_${project.footerText1}|link_type_${linkType}|link_url_${linkUrl.substring(0, 50)}|click_source_card_footer`,
                          value: 1
                        });
                      }}
                    >
                      {project.footerText1}
                    </Card.Link>
                    {project.footerText2 && (
                      <Card.Link 
                        href={project.footerURL2}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Track footer link click with detailed context
                          const linkUrl = project.footerURL2 || 'unknown';
                          const linkType = linkUrl.includes('http') ? 'external' : 'internal';
                          ReactGA.event({
                            category: 'project_card',
                            action: 'footer_link_clicked',
                            label: `${project.title}|link_text_${project.footerText2}|link_type_${linkType}|link_url_${linkUrl.substring(0, 50)}|click_source_card_footer`,
                            value: 1
                          });
                        }}
                      >
                        {project.footerText2}
                      </Card.Link>
                    )}
                  </Card.Footer>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
      
      <ProjectModal
        project={selectedProject}
        show={showModal}
        onHide={(closeMethod) => handleCloseModal(closeMethod)}
        initialImageIndex={initialImageIndex}
        onNavigateProject={(newIndex) => {
          const newProject = projectData[newIndex];
          if (newProject) {
            const anchorId = getProjectAnchorId(newProject.title);
            window.history.replaceState(null, '', `#${anchorId}`);
            setSelectedProject(newProject);
            setInitialImageIndex(0);
            // Keep modal open, just change project
          }
        }}
        projectIndex={selectedProject ? projectData.findIndex(p => p.title === selectedProject.title) : null}
        totalProjects={projectData.length}
      />
    </div>
  );
}
